<script>
export default {
  name: "InsuranceFulfillmentModal",
};
</script>

<script setup>
import { toRefs, ref, computed, watch } from "vue";
import orders from "@/services/orders";
import { useToast } from "vue-toast-notification";
import { cloneDeep } from "lodash";
import Dialog from "primevue/dialog";
import Dropdown from "primevue/dropdown";
import InputNumber from 'primevue/inputnumber';
import ButtonWithSpinner from "@/components/forms/SharedComponents/ButtonWithSpinner";
import SimpleTextarea from "@/components/shared/SimpleTextarea";
import CloseIcon from "@/components/svg-icons/CloseIcon";
import {
  CANCELLATION_REASONS_OTHER,
  CANCELLATION_REASONS,
  ERROR_TIMEOUT, SUCCESS_TIMEOUT,
  MAX_NOTE_TEXTAREA_LIMIT,
  LIBRARY_IDS,
  ORDER_PRODUCT_STATUS,
  OPTIONS_PRICE_OVERRIDE,
} from "@/constants";
import { ERROR_MESSAGE } from "@/components/StudentFulfillment/components/modals/HousingFulfillmentModal/constants";
import { centsToDollars, dollarsToCents } from "@/mixins/helpers";
import { convertCost } from "@/components/program-manager/sessions/utils";
import {
  lineItemPriceHasChanged,
  setPriceOverrideDropdownOption,
  lineItemHasPendingOverride,
  generatePriceOverrideOptions,
  generateDefaultPriceOverride,
  buildStatusRow,
  lineItemCurrentlyApproved,
  canCreatePriceOverride,
  canUpdatePriceOverride,
  findLatestPrice,
  showCurrentPrice,
} from "@/components/StudentFulfillment/components/modals/utils.ts";
import { datadogLogs } from "@datadog/browser-logs";
import { getOverrideReasons } from "@/composables/useOverrideReasons.js"
import { useCreatePriceOverride, getQueryKey as createKey } from "@/providers/priceOverrides/usePriceOverrideCreate";
import { usePatchPriceOverride, getQueryKey as patchKey } from "@/providers/priceOverrides/usePriceOverridePatch";
import { usePriceOverridesRevert } from "@/providers/priceOverrides/usePriceOverridesRevert";
import { useQueryClient } from '@tanstack/vue-query'

const toast = useToast();

const props = defineProps({
  modalIsOpen: {
    type: Boolean,
    default: true,
  },
  orderId: {
    type: String,
    default: "",
  },
  lineItem: {
    type: Object,
    default: () => { },
  },
  profileName: {
    type: String,
    default: "",
  },
  statusOptions: {
    type: Array,
    default: () => [],
  },
  sessionPrices: {
    type: Object,
    default: () => {}
  }
});
const queryClient = useQueryClient();

const { keepPriceReasons, customPriceReasons, } = getOverrideReasons();

const { mutateAsync: createPriceOverrideRequest } = useCreatePriceOverride({
  onSuccess: async (data, variables) => {
    await queryClient.invalidateQueries({ queryKey: createKey() });
    await queryClient.invalidateQueries({ queryKey: ["order"] });
    toast.open({
      message: `Created Price Override for ${variables.name}`,
      type: "success",
      position: "bottom",
      duration: SUCCESS_TIMEOUT,
    });
  },
  onError: (error, variables) => {
    datadogLogs.logger.warn("Failed to create price override.", {
      error: error.message,
    });
    toast.open({
      message: `Couldn't create price override for ${variables.name}`,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  },
});
const { mutateAsync: patchPriceOverrideRequest } = usePatchPriceOverride({
  onSuccess: async ({ id }, variables) => {
    await queryClient.invalidateQueries({ queryKey: patchKey(id) });
    await queryClient.invalidateQueries({ queryKey: ["order"] });
    toast.open({
      message: `Updated Price Override for ${variables.name}`,
      type: "success",
      position: "bottom",
      duration: SUCCESS_TIMEOUT,
    });
  },
  onError: (error, variables) => {
    datadogLogs.logger.warn("Failed to update price override.", {
      error: error.message,
    });
    toast.open({
      message: `Couldn't update price override for ${variables.name}`,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  },
});
const { mutateAsync: revertPriceOverrideRequest } = usePriceOverridesRevert({
    onSuccess: async (data, variables) => {
      if (Array.isArray(data?.errors) && data.errors.length > 0) {
        datadogLogs.logger.warn("Failed to revert price override for ids.", {
          errors: data?.errors,
        });
        toast.open({
          message: `Couldn't revert price override for ${variables.name}`,
          type: "error",
          position: "bottom",
          duration: ERROR_TIMEOUT,
        });
      } else {
        toast.open({
          message: `Reverted price override for ${variables.name}`,
          type: "success",
          position: "bottom",
          duration: ERROR_TIMEOUT,
        });
      }
      await queryClient.invalidateQueries({ queryKey: ["order"] });
    },
    onError: (error, variables) => {
      datadogLogs.logger.warn("Failed calling revert price override.", {
        error: error.message,
        priceOverrideId: variables?.payload ?? "unknown",
      });
      toast.open({
        message: `Couldn't revert price override for ${variables.name}`,
        type: "error",
        position: "bottom",
        duration: ERROR_TIMEOUT,
      });
    },
});


const createPriceOverride = async (line_item) => {
  const createResponse = await createPriceOverrideRequest({
    payload: {
      request_notes: line_item.price_override.request_notes,
      override_amount_in_cents: dollarsToCents(line_item.price_override.override_amount_in_dollars),
      order_id: line_item.price_override.order_id,
      product_id: line_item.id,
      override_reason_id: line_item.price_override.override_reason_id,
      library_id: line_item.price_override.library_id,
    },
    name: line_item?.name,
  });
  return createResponse;
};

const patchPriceOverride = async (line_item) => {
  const updateResponse = await patchPriceOverrideRequest({
    id: line_item.price_override.id,
    payload: {
      override_amount_in_cents: dollarsToCents(line_item.price_override.override_amount_in_dollars),
      override_reason_id: line_item.price_override.override_reason_id,
      request_notes: line_item.price_override.request_notes,
    },
    name: line_item?.name,
  });
  return updateResponse;
};

const revertPriceOverride = async (line_item) => {
  const revertResponse = await revertPriceOverrideRequest({
    payload: {
      decision_notes: "User reverted to original price.",
      id: line_item?.latest_approved_price_override?.id
    },
    name: line_item?.name,
  });
  return revertResponse;
};

const createOrUpdatePriceOverride = async (previous_operation_ok, line_item) => {
  let cuExecuted = false; // c (created), u (updated) = cu
  if (canCreatePriceOverride(previous_operation_ok, line_item)) {
    await createPriceOverride(line_item);
    cuExecuted = true;
  } else if (canUpdatePriceOverride(previous_operation_ok, line_item)) {
    await patchPriceOverride(line_item);
    cuExecuted = true;
  }

  return cuExecuted;
};

const { orderId, modalIsOpen, lineItem, statusOptions, profileName } = toRefs(props);

const editableLineItem = ref({ ...cloneDeep(lineItem.value) });


const isSaving = ref(false);

const emit = defineEmits(["closeModal"]);

const checkOtherCancellationReason = (cancellationReason) =>
  cancellationReason && CANCELLATION_REASONS.findIndex(({ id }) => id === cancellationReason) < 0;

const defaultCancellationReason = ref();
const customCancellationReasonText = ref();

const isCanceledSelected = computed(() => editableLineItem.value?.status === "Canceled");

const lineItemName = computed(() => editableLineItem.value?.misc_product?.name);

const lineItemPrice = computed(() => convertCost(centsToDollars(editableLineItem.value?.total_price_in_cents)));

const handleCloseModal = async (invalidateQuery = false) => {
  // Note: We invalidate the query if the line item was updated successfully but no price override was made|modified
  if (typeof invalidateQuery === "boolean" && invalidateQuery) {
    await queryClient.invalidateQueries({ queryKey: ["order"] });
  }
  emit("closeModal");
};

const meetsMinimumRequrimenets = () => {
  if (
    [ORDER_PRODUCT_STATUS.canceled, ORDER_PRODUCT_STATUS.fulfilled].includes(editableLineItem.value.status) &&
    [OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value, OPTIONS_PRICE_OVERRIDE.CUSTOM_PRICE.value].includes(editableLineItem.value.price_option) &&
    !Boolean(editableLineItem.value.price_override?.override_reason_id)
  ){
    return false;
  }
  return true;
};

const updateItemStatus = async () => {
  // Update: Besides updating status it also creates/updates price overrides
  isSaving.value = true;
  try {
    const cancellation_reason =
      defaultCancellationReason.value === CANCELLATION_REASONS_OTHER ?
        customCancellationReasonText.value :
        defaultCancellationReason.value;

    if (!meetsMinimumRequrimenets()) {
      toast.open({
        message: "We didn’t find any changes to apply. If you meant to create or update a price override, please fill out the required fields.",
        type: "warning",
        position: "bottom",
        duration: ERROR_TIMEOUT,
      });
      return;
    }

    // Needs to update line item
    const use_latest_product_price = findLatestPrice(editableLineItem.value)
    if (props.lineItem?.status !== editableLineItem.value.status || use_latest_product_price) {
      const orderData = await orders.updateOrderLineItem({
        orderId: orderId.value,
        productId: editableLineItem.value.id,
        payload: {
          new_product_id: editableLineItem.value.id,
          status: editableLineItem.value.status,
          quantity: editableLineItem.value.quantity,
          use_latest_product_price,
          cancellation_reason
        },
        skipErrorBus: true,
      });

      toast.open({
        message: "Order item status updated successfully",
        type: "success",
        position: "bottom",
        duration: SUCCESS_TIMEOUT,
      });

      const operationExecuted = await createOrUpdatePriceOverride(true, editableLineItem.value);
      const invalidateQuery = !operationExecuted && Boolean(orderData?.data?.data?.order?.id);
      handleCloseModal(invalidateQuery);
    } else {
      // Simply updating/creating price overrides
      await createOrUpdatePriceOverride(true, editableLineItem.value);
      handleCloseModal();
    }
  } catch (exception) {
    toast.open({
      message: `${ERROR_MESSAGE}`,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
    handleCloseModal();
  } finally {
    isSaving.value = false;
  }
};


const buildInsurance = (line_item) => {
  const current_session_pricing = lineItemPriceHasChanged(line_item, props.sessionPrices?.additional_fees, LIBRARY_IDS.miscProducts);
  const price_dropdown_default_option = setPriceOverrideDropdownOption(line_item, current_session_pricing);
  const price_options = generatePriceOverrideOptions(line_item, current_session_pricing, price_dropdown_default_option);
  const pending_price_override = lineItemHasPendingOverride(line_item) ? line_item.latest_unapproved_price_override : {};

  return {
    current_session_pricing: current_session_pricing,
    current_price_config: showCurrentPrice(pending_price_override?.id, current_session_pricing?.total_price_in_cents, line_item?.total_price_in_cents),
    pending_price_override: !!pending_price_override?.id,
    total_price: Boolean(Number.isFinite(line_item?.total_price_in_cents)) ? `${convertCost(centsToDollars(line_item?.total_price_in_cents))}` : line_item?.product_price,
    price_options: price_options,
    price_option: price_dropdown_default_option,
    price_override: generateDefaultPriceOverride(line_item, price_dropdown_default_option, pending_price_override, LIBRARY_IDS.miscProducts, orderId.value),
    price_override_status: buildStatusRow(line_item),
    previously_approved: lineItemCurrentlyApproved(line_item),
    name: line_item?.misc_product?.name ?? "",
    disable_price_dropdown: Boolean(pending_price_override?.id),
  };
}

watch(editableLineItem, (item) => {
  defaultCancellationReason.value = checkOtherCancellationReason(item?.cancellation_reason)
    ? CANCELLATION_REASONS_OTHER
    : item?.cancellation_reason;
  customCancellationReasonText.value = checkOtherCancellationReason(item?.cancellation_reason)
    ? item?.cancellation_reason
    : undefined;

  editableLineItem.value.latest_approved_price_override = lineItem.value?.price_override;
  Object.assign(editableLineItem.value, buildInsurance(editableLineItem.value));
},
{
  immediate: true,
});

const modalHeaderPassthrough = {
  root: {
    class: "w-3/5",
  },
  header: {
    class: ["bg-emerald-50", "min-h-65", "flex", "justify-between", "items-center", "pl-5", "pr-2", "rounded-t-lg", "border", "border-white"],
  },
}

const updatePriceOverride = () => {
  // Triggers how the price override should be presented
  const latest_unapproved_po = lineItemHasPendingOverride(editableLineItem.value) ? editableLineItem.value.latest_unapproved_price_override : {};
  editableLineItem.value.price_override = generateDefaultPriceOverride(editableLineItem.value, editableLineItem.value.price_option, latest_unapproved_po, LIBRARY_IDS.miscProducts, orderId.value);
};

const resetPricingOption = () => {
  // Triggers which option will be set in the price dropdown
  const latest_unapproved_po = lineItemHasPendingOverride(editableLineItem.value) ? editableLineItem.value.latest_unapproved_price_override : {};
  editableLineItem.value.price_option = setPriceOverrideDropdownOption(editableLineItem.value, editableLineItem.value?.current_session_pricing);
  editableLineItem.value.price_override = generateDefaultPriceOverride(editableLineItem.value, editableLineItem.value.price_option, latest_unapproved_po, LIBRARY_IDS.miscProducts, orderId.value);
};

const cancelOrRevert = async (line_item) => {
  // MVP: Just revert, cancel will come later
  // if (lineItemHasPendingOverride(line_item)) {
  // ....
  //}
  await revertPriceOverride(line_item);
  handleCloseModal();
};

</script>

<template>
  <div>
    <div v-if="modalIsOpen" class="relative z-50">
      <Dialog :visible="modalIsOpen" :modal="true" @update:visible="handleCloseModal" :pt="modalHeaderPassthrough" dismissableMask>
        <template #header>
          <div class="font-semibold font-montserrat text-slate-700">
            {{ profileName ? `${profileName}'s ` : "" }}Insurance
          </div>
        </template>
        <template #closeicon>
          <CloseIcon size="18" stroke="#037f80" stroke-width="1.75" />
        </template>
        <div class="mx-auto bg-white z-50 relative">
          <div class="max-h-96 overflow-auto">
            <!-- Content -->
             <div class="grid grid-cols-3 font-montserrat text-sm">
                <!-- Header -->
                <div class="text-left font-semibold py-3 px-2 bg-gray-125 border-b-[1px] border-solid border-b-gray-200">
                  Name
                </div>
                <div class="text-left font-semibold py-3 px-2 bg-gray-125 border-b-[1px] border-solid border-b-gray-200">
                  Price
                </div>
                <div class="text-left font-semibold py-3 px-2 bg-gray-125 border-b-[1px] border-solid border-b-gray-200">
                  Status
                </div>

                <!-- Content -->
                <div class="px-2 mt-2">
                  {{ lineItemName }}
                </div>
                <div class="px-2 mt-2">
                  <div v-if="ORDER_PRODUCT_STATUS.fulfilled === editableLineItem.status">
                    <Dropdown
                      v-model="editableLineItem.price_option"
                      :disabled="editableLineItem.disable_price_dropdown"
                      :options="editableLineItem?.price_options"
                      optionLabel="label"
                      optionValue="value"
                      optionDisabled="disabled"
                      placeholder="Select price"
                      class="w-full"
                      @change="updatePriceOverride"
                    />
                  </div>
                  <p v-else>
                  {{ lineItemPrice }}
                  </p>
                </div>
                <div class="px-2 mt-2">
                  <Dropdown
                    v-model="editableLineItem.status"
                    :disabled="isSaving || editableLineItem.disable_price_dropdown"
                    :options="statusOptions"
                    placeholder="Select status"
                    class="w-full"
                    @change="resetPricingOption"
                  />
                </div>

              <!-- Price for cancelled -->
              <div
                class="col-start-2 col-end-4 flex items-center justify-end pb-6 mt-4"
                v-if="editableLineItem.status === 'Canceled'"
              >
                <label
                  class="mr-2 text-sm font-semibold basis-auto shrink-0 grow-1"
                  :for="`price-for-canceled-editableLineItem-${editableLineItem.id}`"
                >
                  Price of Canceled Insurance
                </label>
                  <Dropdown
                    v-model="editableLineItem.price_option"
                    :disabled="editableLineItem.disable_price_dropdown"
                    :options="editableLineItem?.price_options"
                    optionLabel="label"
                    optionValue="value"
                    :inputId="`price-for-canceled-editableLineItem-${editableLineItem.id}`"
                    placeholder="Select price"
                    class="w-full"
                    @change="updatePriceOverride"
                  />
              </div>

              <div class="col-start-2 col-end-4 flex items-center justify-start pb-6 mt-4" v-if="editableLineItem.price_option === OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value && editableLineItem.current_price_config.display">
                <div class="text-sm font-semibold">
                  Current Product Price: <span class="font-normal">{{ editableLineItem.current_price_config.currentPriceFormatted }}</span>
                </div>
              </div>
              <!-- Override reason for keep order-->
              <div class="col-start-2 col-end-4 flex items-center justify-end pb-6 mt-2" v-if="editableLineItem.price_option === OPTIONS_PRICE_OVERRIDE.KEEP_ORDER.value && [ORDER_PRODUCT_STATUS.fulfilled, ORDER_PRODUCT_STATUS.canceled].includes(editableLineItem.status)">
                <label
                  class="mr-2 text-sm font-semibold basis-auto shrink-0 grow-1"
                  :for="`keep-price-reason-${editableLineItem.id}`"
                >
                  Keep Order Price Reason
                </label>
                <Dropdown
                  v-model="editableLineItem.price_override.override_reason_id"
                  class="w-full grow shrink-1 basis-0"
                  placeholder="Select a reason."
                  :options="keepPriceReasons.misc.value"
                  :inputId="`keep-price-reason-${editableLineItem.id}`"
                  optionLabel="name"
                  optionValue="id"
                  :invalid="!editableLineItem.price_override.override_reason_id"
                >
                  <template #option="optionSlotProps">
                    <p>
                      {{ optionSlotProps.option.name }}
                    </p>
                    <p class="ml-4 mt-2 text-gray-600 text-sm">
                      {{ optionSlotProps.option.description }}
                    </p>
                  </template>
                </Dropdown>
              </div>

                <!-- Custom Price Override fields-->
              <div class="col-start-2 col-end-4 flex flex-col pb-6 mt-2" v-if="editableLineItem.price_option === OPTIONS_PRICE_OVERRIDE.CUSTOM_PRICE.value">
                  <div class="flex items-center justify-end w-full mb-6 gap-4">
                    <div class="text-sm font-semibold">
                      Current Product Price: <span class="font-normal">{{ editableLineItem.total_price }}</span>
                    </div>

                    <div class="flex items-center gap-2">
                      <label class="text-sm font-semibold">New Price $</label>
                      <InputNumber
                        v-model="editableLineItem.price_override.override_amount_in_dollars"
                        inputId="minmaxfraction"
                        :minFractionDigits="2"
                        :maxFractionDigits="2"
                        :min="0"
                      />
                    </div>
                  </div>

                  <!-- Override reason -->
                  <div class="flex mb-6">
                    <label class="text-sm font-semibold basis-auto shrink-0 grow-1 mr-4">Override Reason</label>
                    <Dropdown
                        v-model="editableLineItem.price_override.override_reason_id"
                        class="w-full grow shrink-1 basis-0"
                        placeholder="Select a reason."
                        :options="customPriceReasons.misc.value"
                        :inputId="`custom-price-reason-${editableLineItem.id}`"
                        optionLabel="name"
                        optionValue="id"
                        :invalid="!editableLineItem.price_override.override_reason_id"
                      >
                        <template #option="optionSlotProps">
                          <p>
                            {{ optionSlotProps.option.name }}
                          </p>
                          <p class="ml-4 mt-2 text-gray-600 text-sm">
                            {{ optionSlotProps.option.description }}
                          </p>
                        </template>
                      </Dropdown>
                  </div>

                  <!-- Notes -->
                  <div class="flex">
                    <label class="text-sm font-semibold basis-auto shrink-0 grow-1 mr-4">Additional Notes</label>
                    <SimpleTextarea
                      v-model="editableLineItem.price_override.request_notes"
                      label-name="Additional Notes"
                      outer-classes="w-full grow shrink-1 basis-0"
                      :max-length="MAX_NOTE_TEXTAREA_LIMIT"
                      :disabled="isSaving"
                    />
                  </div>
              </div>

                <!-- Cancelation -->
                <div v-if="isCanceledSelected" class="col-start-2 col-end-4">
                  <div class="flex">
                    <label
                      class="mr-2 text-sm font-semibold basis-auto shrink-0 grow-1"
                      for="insurance-modal-cancelation-reason"
                    >
                      Reason for Cancellation
                    </label>
                    <Dropdown
                      v-model="defaultCancellationReason"
                      inputId="insurance-modal-cancelation-reason"
                      class="w-full grow shrink-1 basis-0"
                      optionLabel="label"
                      optionValue="label"
                      :options="CANCELLATION_REASONS"
                      :disabled="isSaving"
                    />
                  </div>
                </div>
                <div v-if="isCanceledSelected && defaultCancellationReason === CANCELLATION_REASONS_OTHER" class="col-start-2 col-end-4">
                  <SimpleTextarea
                    id="insurance-modal-custom-reason"
                    v-model="customCancellationReasonText"
                    class="mt-5"
                    label-name="Cancellation Reason"
                    :max-length="MAX_NOTE_TEXTAREA_LIMIT"
                    :disabled="isSaving"
                  />
                </div>

                <div class="col-start-2 col-end-4 flex flex pb-6 gap-2 font-montserrat mt-2" v-if="editableLineItem.price_override_status.display">
                  <p class="text-sm font-semibold">Override Request Status: </p>
                  <!-- Override Icon -->
                  <span class="material-symbols-outlined" :class="editableLineItem.price_override_status.labelColor">
                    {{ editableLineItem.price_override_status.icon}}
                  </span>
                  <p> {{ editableLineItem.price_override_status.labelStatus }} </p>
                  <p>
                    <span v-if="editableLineItem.price_override_status.displayRevert" class="uppercase text-sm font-semibold text-teal-900 cursor-pointer" @click.prevent="cancelOrRevert(editableLineItem)">
                      | {{ editableLineItem.price_override_status.revertLabel }}
                    </span>
                  </p>
                </div>
             </div>
          </div>

          <div class="mt-8 flex justify-end font-semibold">
            <ButtonWithSpinner id="cancel-btn" ref="cancelBtn" data-cy="cancel-btn" variant="secondary-outlined"
              custom-class="mr-5 w-107px" @click="handleCloseModal">
              <span>Cancel </span>
            </ButtonWithSpinner>
            <ButtonWithSpinner :prop-loading="isSaving" id="save-btn" ref="saveBtn" data-cy="save-order-room-button"
              variant="secondary" variant-type="normal" custom-class="w-107px" @click="updateItemStatus">
              <span>Save</span>
            </ButtonWithSpinner>
          </div>
        </div>
      </Dialog>
    </div>
  </div>
</template>
